import React from "react";
import ReactDOM from "react-dom";

import axios from "./axios-claps";
import * as cocoSsd from "@tensorflow-models/coco-ssd";
import "@tensorflow/tfjs";
import "./styles.css";

class App extends React.Component {
  state = {
    claps: 0,
    show: false
  };

  videoRef = React.createRef();
  canvasRef = React.createRef();

  componentDidMount() {
    const cursor = document.querySelector('.cursor');

    document.addEventListener('mousemove', e => {
      cursor.setAttribute("style", "top: " + (e.pageY - 10) + "px; left: " + (e.pageX - 13) + "px;")
    })

    document.addEventListener('click', () => {
      cursor.classList.add("expand");

      setTimeout(() => {
        cursor.classList.remove("expand");
      }, 300)
    })
    axios.get("/claps.json").then(response => this.setState({claps: response.data}))
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      const webCamPromise = navigator.mediaDevices
        .getUserMedia({
          audio: false,
          video: {
            facingMode: "user"
          }
        })
        .then(stream => {
          window.stream = stream;
          this.videoRef.current.srcObject = stream;
          return new Promise((resolve, reject) => {
            this.videoRef.current.onloadedmetadata = () => {
              resolve();
            };
          });
        });
      const modelPromise = cocoSsd.load();
      Promise.all([modelPromise, webCamPromise])
        .then(values => {
          this.detectFrame(this.videoRef.current, values[0]);
        })
        .catch(error => {
          console.error(error);
        });
    }
  }

  detectFrame = (video, model) => {
    model.detect(video).then(predictions => {
      this.renderPredictions(predictions);
      requestAnimationFrame(() => {
        this.detectFrame(video, model);
      });
    });
  };

  renderPredictions = predictions => {
    const ctx = this.canvasRef.current.getContext("2d");
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    // Font options.
    const font = "20px sans-serif";
    ctx.font = font;
    ctx.textBaseline = "top";
    predictions.forEach(prediction => {
      const x = prediction.bbox[0];
      const y = prediction.bbox[1];
      const width = prediction.bbox[2];
      const height = prediction.bbox[3];
      // Draw the bounding box.
      const gradient = ctx.createLinearGradient(0, 0, ctx.canvas.width, ctx.canvas.height);
      gradient.addColorStop("0.1", "magenta");
      gradient.addColorStop("0.5", "#23a6d5");
      gradient.addColorStop("0.9", "#23d5ab");
      ctx.strokeStyle = gradient;
      ctx.lineWidth = 6;
      ctx.strokeRect(x, y, width, height);
      // Draw the label background.
      ctx.fillStyle = gradient;
      const tag = prediction.class === 'person' ? 'fellow human being 😄' : prediction.class === 'tv' ? 'screen' : prediction.class
      const textWidth = ctx.measureText(tag).width;
      const textHeight = parseInt(font, 10); // base 10
      ctx.fillRect(x, y, textWidth + 14, textHeight + 14);
      // Draw the text.
      ctx.fillStyle = "#ffffff";
      ctx.fillText(tag, x + 6, y + 6);
    });
  };

  addClap = () => {
    const newClaps = this.state.claps + 1;
    this.setState({claps : newClaps})
    axios.put('/claps.json', newClaps);
  };

  render() {
    return (
      <div>
        <div className="cursor"></div>
        <h1>Live Object Detection</h1>
        <h3>Using Tensorflow, Firebase and React</h3>
        <div className="canvasContainer">
          <div className="videoCanvas">
            <p className="loader">A webcam or camera is required for live object detection<br></br>Please wait while the model is loaded...</p>
            <video
              className="size"
              autoPlay
              playsInline
              muted
              ref={this.videoRef}
              width="640"
              height="480"
            />
            <canvas
              className="size"
              ref={this.canvasRef}
              width="640"
              height="480"
            />
            <div className="overlay">
              <p>
                <strong>Oops!</strong> This configuration is currently not fully supported on mobile devices.
                <br></br>
                <br></br>
                To ensure an optimal experience, please use the <strong>desktop</strong> version of the <strong>Google Chrome</strong> browser.
                <br></br>
                <br></br>
                <strong>Feeling hacky?</strong> Try turning your device to landscape mode.
              </p>
            </div>
          </div>
        </div>
        <div className="claps">
          <div className="padding"></div>
          <button onClick={this.addClap}><img src="https://static.thenounproject.com/png/1471176-200.png" alt="Clap for Luca Bontempi's project" /></button>
          <p>{this.state.claps}</p>
        </div>
        <div className="cta">
          <a href="https://nvios.github.io/luca_bontempi/" className="button">Visit Website</a>
          <a href="mailto:nvios.lab@gmail.com" className="button right">Contact Me</a>
        </div>
        <div className='description'>
          <hr>
          </hr>
          <p>
            This demo was develped by <a href="https://nvios.github.io/luca_bontempi/" target="_blank" rel="noopener noreferrer" className="inline"><strong>Luca Bontempi</strong></a> using Tensorflow.js, Firebase and React as an experiment to integrate a deep learning model and realtime database access into a frontend application.
          </p>
          <p>            
            To ensure an optimal experience, please use the desktop version of the Google Chrome browser.
          </p>
          <p>
            This implementation is based on a training
            instance of the COCO dataset with over 80 labels (e.g. vehicles, animals and household objects). The experiment introduces several improvements over my  
            previous <a href="https://github.com/nvios/facial_recognition" target="_blank" rel="noopener noreferrer" className="inline"><strong> computer vision project </strong></a> for
            real-time face recognition, written in Python and based on a Multi-task Cascaded Convolutional Neural Network (MTCNN).</p>
          <p>
            If you are curious about my other demos and Data Science projects, you can check out my <a href="https://nvios.github.io/luca_bontempi/" target="_blank" rel="noopener noreferrer" className="inline"><strong>
            website</strong></a> and <a href="https://github.com/nvios/" target="_blank" rel="noopener noreferrer" className="inline"><strong>Github</strong></a> page.
          </p>
          <p className="tags">
            <span className="big">#</span> Artificial Intelligence &nbsp;|&nbsp; AI &nbsp;|&nbsp; Neural Networks &nbsp;|&nbsp; Computer Vision &nbsp;|&nbsp; Databases &nbsp;|&nbsp; Cloud Computing
          </p>
          <p className="copyright">&copy; Copyright 2020 &nbsp;|&nbsp; Luca Bontempi</p>
        </div>
      </div>
    );
  }
}

const rootElement = document.getElementById("root");
ReactDOM.render(<App />, rootElement);
